import React, { createContext, useState, useContext, useEffect } from "react";
import { supabase } from "../services/supabase";
import { siteConfig } from "../config/siteConfig";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    if (!user?.id) return null;

    console.log("🔄 Refrescando datos del perfil...");
    const updatedProfile = await getProfile(user.id);

    if (updatedProfile) {
      setProfile(updatedProfile);
      setIsAuthenticated(true);
    }
    return updatedProfile;
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    if (error) throw error;
    return data;
  };

  const getProfile = async (userId) => {
    try {
      console.log("1. Entrando a getProfile...");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("4. Error específico de consulta:", error);
        return null;
      }

      console.log("5. Éxito final:", data);
      return data;
    } catch (err) {
      console.error("💥 Error atrapado en catch:", err);
      return null;
    }
  };

  // useEffect corregido para evitar ejecuciones dobles
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user && isMounted) {
          setUser(session.user);
          const p = await getProfile(session.user.id);
          setProfile(p);
          setIsAuthenticated(!!p);
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) setLoading(false); // <--- Esto garantiza que el loading se quite siempre
      }
    };

    initialize();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`🔄 Evento Auth: ${event}`);

      // ❌ ELIMINADO: No más auto-login en SIGNED_IN
      // if (event === "SIGNED_IN" && session?.user) {
      //   setUser(session.user);
      //   const p = await getProfile(session.user.id);
      //   setProfile(p);
      //   setIsAuthenticated(true);
      // }

      if (event === "SIGNED_OUT") {
        setUser(null);
        setProfile(null);
        setIsAuthenticated(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ============================================
  // CREAR PERFIL
  // ============================================
  const createProfile = async (userId, email, userData = {}) => {
    try {
      console.log(`🆕 ===== CREATE PROFILE INICIADO =====`);
      console.log(`📧 Email: ${email}`);
      console.log(`🆔 UserId: ${userId}`);
      console.log(`📱 Teléfono recibido:`, userData.phone);

      // 1. VERIFICAR SI EL PERFIL YA EXISTE
      const { data: existingProfile, error: checkError } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (checkError) {
        console.error(`❌ Error verificando perfil existente:`, checkError);
      }

      //if (existingProfile) {
      //console.log(`✅ El perfil YA EXISTE:`, existingProfile);
      //return existingProfile;
      //}

      // 2. PREPARAR DATOS DEL PERFIL
      const newProfile = {
        id: userId,
        email: email,
        name: userData.name || email.split("@")[0] || "Usuario",
        phone: userData.phone || "",
        profession: siteConfig.niche.aspirantLabel,
        bio: "",
        avatar_url: null,
        join_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        exam_count: 0,
        best_score: 0,
        progress: 0,
        saas: siteConfig.saasKey || null,
      };

      // console.log(`📱 OBJETO 4:`, newProfile);
      // 3. INSERTAR PERFIL
      const { data, error } = await supabase
        .from("profiles")
        .insert([newProfile])
        .select()
        .single();

      if (error) {
        console.error(`❌ Error al insertar perfil:`, error);

        if (error.code === "23505") {
          console.log(`⚠️ Conflicto de clave, intentando upsert...`);
          const { data: upsertData, error: upsertError } = await supabase
            .from("profiles")
            .upsert([newProfile])
            .select()
            .single();

          if (upsertError) {
            console.error(`❌ Upsert también falló:`, upsertError);
            throw upsertError;
          }

          return upsertData;
        }
        throw error;
      }

      console.log(`✅ Perfil creado exitosamente:`, data);
      return data;
    } catch (error) {
      console.error(`💥 Error CRÍTICO en createProfile:`, error);
      return null;
    }
  };

  // ============================================
  // LOGIN - ÚNICA FORMA DE AUTENTICARSE
  // ============================================
  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      let profileData = await getProfile(data.user.id);

      if (!profileData) {
        profileData = await createProfile(
          data.user.id,
          email,
          data.user.user_metadata || {}
        );
      }

      setUser(data.user);
      setProfile(profileData);
      setIsAuthenticated(true);

      return {
        success: true,
        user: data.user,
        profile: profileData,
      };
    } catch (error) {
      console.error("❌ Error en login:", error.message);
      return { success: false, error: error.message };
    } finally {
    }
  };

  // ============================================
  // REGISTRO - SOLO CREA, NO AUTENTICA
  // ============================================
  const register = async (email, password, userData) => {
    try {
      console.log("\n🔵 ===== REGISTRO INICIADO =====");
      console.log("📝 Datos:", { email, userData });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone || "",
          },
        },
      });

      if (error) throw error;

      console.log(`✅ Usuario creado en Auth:`, {
        id: data.user?.id,
        email: data.user?.email,
      });

      if (data.user) {
        // Crear perfil
        console.log(`🆕 Creando perfil para ${data.user.id}...`);
        const profileData = await createProfile(data.user.id, email, {
          name: userData.name,
          phone: userData.phone || "",
        });

        if (profileData) {
          console.log(`✅ Perfil creado exitosamente`);

          return {
            success: true,
            user: data.user,
            profile: profileData,
            message: "¡Registro exitoso! Ahora puedes iniciar sesión.",
          };
        }
      }

      await supabase.auth.signOut();

      return {
        success: true,
        user: data.user,
        message: "Registro exitoso. Ya puedes acceder a la plataforma.",
      };
    } catch (error) {
      console.error(`❌ Error en registro:`, error);
      return { success: false, error: error.message };
    } finally {
      console.log(`🏁 ===== REGISTRO FINALIZADO =====\n`);
    }
  };

  // ============================================
  // LOGOUT
  // ============================================
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("❌ Error en logout:", error.message);
      return { success: false, error: error.message };
    }
  };

  // ============================================
  // ACTUALIZAR PERFIL
  // ============================================
  const updateProfile = async (updatedData) => {
    try {
      if (!user) throw new Error("No hay usuario autenticado");

      // ✅ CAMBIO CLAVE: Usamos el objeto spread (...updatedData)
      // para enviar CUALQUIER campo que venga en la llamada
      const { data, error } = await supabase
        .from("profiles")
        .update({
          ...updatedData, // Esto incluye automáticamente read_laws, progress, etc.
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)
        .select()
        .single();

      if (error) throw error;

      // Actualizamos el estado global para que React redibuje los componentes
      setProfile(data);

      return {
        success: true,
        profile: data,
        message: "Perfil actualizado correctamente",
      };
    } catch (error) {
      console.error("❌ Error actualizando perfil:", error);
      return { success: false, error: error.message };
    }
  };

  // ============================================
  // VALOR DEL CONTEXTO
  // ============================================
  const value = {
    user,
    profile,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
